import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User as FirebaseUser, signInAnonymously, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { User } from '../types';
import { joinRoomAsParticipant, leaveRoom, updateUserLastSeen } from '../utils/roomManager';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  participantId: string | null;
  login: (profile: { name: string; role: string; avatar: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateUserRole: (role: string) => Promise<void>;
  joinRoom: (roomId: string, profile: { name: string; role: string; avatar: string }, isAdmin?: boolean) => Promise<void>;
  leaveCurrentRoom: () => Promise<void>;
  updateLastSeen: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (profile: { name: string; role: string; avatar: string }) => {
    try {
      // Starting login process with profile
      const result = await signInAnonymously(auth);
      // Firebase auth successful
      
      const userData: User = {
        id: result.user.uid,
        name: profile.name,
        email: result.user.email || '',
        role: profile.role as any,
        avatar: profile.avatar,
        isAdmin: false
      };
      
      // Store user data in localStorage instead of database
      localStorage.setItem('currentUser', JSON.stringify(userData));
      setCurrentUser(userData);
      // User data saved and state updated
    } catch (error) {
      // Error during login
      throw error;
    }
  };

  const logout = async () => {
    if (participantId) {
      await leaveCurrentRoom();
    }
    await signOut(auth);
    // Clear localStorage
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setParticipantId(null);
  };

  const updateUserRole = async (role: string) => {
    if (!currentUser) return;
    
    const updatedUser = { ...currentUser, role: role as any };
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    setCurrentUser(updatedUser);
  };

  const joinRoom = async (roomId: string, profile: { name: string; role: string; avatar: string }, isAdmin = false) => {
    if (!currentUser) {
      // Cannot join room: User not authenticated
      return;
    }
    
    // Joining room with user and profile
    
    try {
      // Always leave current room first to prevent duplicates
      if (participantId) {
        // Leaving current room first
        await leaveCurrentRoom();
        // Add delay to ensure leave operation completes
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Store room info in localStorage before joining
      localStorage.setItem('currentRoomId', roomId);
      localStorage.setItem('planningPokerProfile', JSON.stringify(profile));
      
      const newParticipantId = await joinRoomAsParticipant(roomId, currentUser.id, profile, isAdmin);
      // Got new participantId
      setParticipantId(newParticipantId);
      localStorage.setItem('participantId', newParticipantId);
      // Successfully joined room as participant
      // State after join updated
    } catch (error) {
      // Error joining room
      throw error;
    }
  };

  const leaveCurrentRoom = async () => {
    // Leave room process started
    // Getting current room ID and user ID
    
    const currentRoomId = localStorage.getItem('currentRoomId');
    const currentUserId = currentUser?.id;
    
    // Preparing to call leaveRoom
    
    if (currentRoomId) {
      try {
        // Always attempt to leave, even if participantId is missing
        await leaveRoom(participantId || '', currentRoomId, currentUserId);
        // Successfully called leaveRoom
      } catch (error) {
        // Error leaving room
      }
    } else {
      // No currentRoomId found, cannot leave room
    }
    
    // Clear all room and user state
    // Clearing state and localStorage
    setParticipantId(null);
    setCurrentUser(null);
    
    // Clear ALL localStorage for fresh start
    localStorage.clear();
    
    // Sign out from Firebase auth to completely reset session and force new anonymous user
    try {
      await signOut(auth);
      // Successfully signed out from Firebase
    } catch (error) {
      // Error signing out
    }
    
    // Leave room process completed
  };

  const updateLastSeen = useCallback(async () => {
    if (participantId) {
      await updateUserLastSeen(participantId);
    }
  }, [participantId]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        // Get user data from localStorage instead of database
        const savedUserData = localStorage.getItem('currentUser');
        if (savedUserData) {
          const userData = JSON.parse(savedUserData) as User;
          setCurrentUser(userData);
          
          // Restore participant ID from localStorage
          const savedParticipantId = localStorage.getItem('participantId');
          if (savedParticipantId) {
            setParticipantId(savedParticipantId);
          }
        }
      } else {
        setCurrentUser(null);
        setParticipantId(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Update last seen every 30 seconds
  useEffect(() => {
    if (!participantId) return;
    
    const interval = setInterval(updateLastSeen, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [participantId, updateLastSeen]);

  const value = {
    currentUser,
    loading,
    participantId,
    login,
    logout,
    updateUserRole,
    joinRoom,
    leaveCurrentRoom,
    updateLastSeen
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};